import { useCallback, useEffect, useMemo, useState } from 'react'
import { HeartHandshake, Loader2 } from 'lucide-react'

import { ReportFilterBar } from '@/components/reports/ReportFilterBar'
import {
  exportCrmReport,
  fetchCrmReport,
  formatReportDate,
  formatReportMoney,
  resolveReportPeriodBounds,
} from '@/lib/api/reports'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { useBranch } from '@/contexts/BranchContext'
import { useReportPanelFilters } from '@/contexts/ReportPreferencesContext'

const BIRTHDAY_WINDOWS = [
  { id: 7, label: '7 days' },
  { id: 30, label: '30 days' },
  { id: 90, label: '90 days' },
]

export function CrmReportPanel() {
  const { activeBranchId } = useBranch()
  const { period, setPeriod, compare, setCompare, customFrom, setCustomFrom, customTo, setCustomTo } =
    useReportPanelFilters()
  const [birthdayDays, setBirthdayDays] = useState(30)
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')

  const bounds = useMemo(() => {
    try {
      return resolveReportPeriodBounds(period, { from: customFrom, to: customTo })
    } catch {
      return null
    }
  }, [period, customFrom, customTo])

  const reportOptions = useMemo(
    () => ({
      period,
      from: period === 'custom' ? customFrom : bounds?.from,
      to: period === 'custom' ? customTo : bounds?.to,
      compare,
      birthdayDays,
    }),
    [period, customFrom, customTo, bounds, compare, birthdayDays],
  )

  const loadReport = useCallback(async () => {
    if (period === 'custom' && (!customFrom || !customTo)) {
      setReport(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError('')

    try {
      const data = await fetchCrmReport(reportOptions)
      setReport(data)
    } catch (err) {
      setReport(null)
      setError(apiErrorMessage(err, 'Unable to load CRM report.'))
    } finally {
      setLoading(false)
    }
  }, [period, customFrom, customTo, reportOptions])

  useEffect(() => {
    loadReport()
  }, [loadReport, activeBranchId])

  const handleExport = async (format) => {
    setExporting(true)
    setError('')
    try {
      await exportCrmReport(format, reportOptions)
    } catch (err) {
      setError(apiErrorMessage(err, `Unable to export ${format.toUpperCase()} report.`))
    } finally {
      setExporting(false)
    }
  }

  const hasActivity =
    (report?.summary?.repeat_customer_count ?? 0) > 0 ||
    (report?.summary?.birthday_count ?? 0) > 0

  return (
    <section className="card-surface p-6 lg:p-8">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <HeartHandshake className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h2 className="text-lg font-semibold tracking-tight">CRM</h2>
            <p className="text-sm text-muted-foreground">
              Repeat customers, upcoming birthdays, and loyalty liability snapshot.
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-3">
          <div className="flex flex-wrap justify-end gap-2">
            {BIRTHDAY_WINDOWS.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setBirthdayDays(item.id)}
                className={`rounded-xl px-3 py-1.5 text-sm font-medium transition-colors ${
                  birthdayDays === item.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted/70 hover:bg-muted'
                }`}
              >
                Birthdays · {item.label}
              </button>
            ))}
          </div>
          <ReportFilterBar
            period={period}
            compare={compare}
            customFrom={customFrom}
            customTo={customTo}
            onPeriodChange={setPeriod}
            onCompareChange={setCompare}
            onCustomFromChange={setCustomFrom}
            onCustomToChange={setCustomTo}
            onExportCsv={() => handleExport('csv')}
            onExportPdf={() => handleExport('pdf')}
            exporting={exporting}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading report…
        </div>
      ) : error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : report ? (
        <>
          <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <SummaryCard
              label="Repeat customers"
              value={String(report.summary.repeat_customer_count)}
            />
            <SummaryCard
              label="Repeat spend"
              value={`AED ${formatReportMoney(report.summary.repeat_spend_total)}`}
            />
            <SummaryCard label="Upcoming birthdays" value={String(report.summary.birthday_count)} />
            <SummaryCard
              label="Loyalty liability"
              value={
                report.summary.loyalty_available
                  ? `AED ${formatReportMoney(report.summary.loyalty_liability)}`
                  : 'Phase 10'
              }
            />
          </div>

          {report.loyalty_liability?.note ? (
            <p className="mb-4 text-sm text-muted-foreground">{report.loyalty_liability.note}</p>
          ) : null}

          <div className="mb-6 grid gap-6 lg:grid-cols-2">
            <div className="overflow-x-auto rounded-2xl border border-border/70">
              <p className="border-b border-border/60 bg-muted/20 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Repeat customers (2+ visits in period)
              </p>
              <table className="min-w-full text-sm">
                <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Customer</th>
                    <th className="px-4 py-3 font-medium">Visits</th>
                    <th className="px-4 py-3 font-medium">Spend</th>
                  </tr>
                </thead>
                <tbody>
                  {report.repeat_customers.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-4 py-6 text-center text-muted-foreground">
                        No repeat customers in this period.
                      </td>
                    </tr>
                  ) : (
                    report.repeat_customers.map((row) => (
                      <tr key={row.customer_id} className="border-t border-border/60">
                        <td className="px-4 py-3">
                          <div className="font-medium">{row.customer_name}</div>
                          <div className="text-xs text-muted-foreground">{row.phone ?? '—'}</div>
                        </td>
                        <td className="px-4 py-3">{row.visit_count}</td>
                        <td className="px-4 py-3">AED {formatReportMoney(row.spend_total)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="overflow-x-auto rounded-2xl border border-border/70">
              <p className="border-b border-border/60 bg-muted/20 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Upcoming birthdays (next {birthdayDays} days)
              </p>
              <table className="min-w-full text-sm">
                <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Customer</th>
                    <th className="px-4 py-3 font-medium">DOB</th>
                    <th className="px-4 py-3 font-medium">In</th>
                  </tr>
                </thead>
                <tbody>
                  {report.birthdays.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-4 py-6 text-center text-muted-foreground">
                        No birthdays in this window.
                      </td>
                    </tr>
                  ) : (
                    report.birthdays.map((row) => (
                      <tr key={row.customer_id} className="border-t border-border/60">
                        <td className="px-4 py-3">
                          <div className="font-medium">{row.customer_name}</div>
                          <div className="text-xs text-muted-foreground">
                            {row.marketing_consent ? 'Marketing OK' : 'No consent'}
                          </div>
                        </td>
                        <td className="px-4 py-3">{formatReportDate(row.dob)}</td>
                        <td className="px-4 py-3">
                          {row.days_until === 0 ? 'Today' : `${row.days_until} days`}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {!hasActivity ? (
            <p className="text-sm text-muted-foreground">
              Last visit dates use completed sales between {formatReportDate(report.from)} and{' '}
              {formatReportDate(report.to)}.
            </p>
          ) : null}
        </>
      ) : null}
    </section>
  )
}

/**
 * @param {{ label: string, value: string }} props
 */
function SummaryCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-border/70 bg-muted/20 px-4 py-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-semibold">{value}</p>
    </div>
  )
}
